import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CiInstagram, CiLinkedin } from 'react-icons/ci';
import { submitContactForm } from './services/contactService';
import './letstalk.css';
import './letstalk-mid.css';
import './letstalk-details.css';
import './animations.css';
import useScrollAnimation from './hooks/useScrollAnimation';

import { logContactFormSubmit } from './services/analyticsService';

export default function LetsTalk() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [detailsRef, detailsVisible] = useScrollAnimation(0.2);
  const [formRef, formVisible] = useScrollAnimation(0.2);

  function showToast(message, type = "info") {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: "", type: "" });
    }, 4000);
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Remove focus from select element after choice to revert the arrow icon
    if (e.target.tagName === 'SELECT') {
      e.target.blur();
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Validate required fields
      if (!formData.name || !formData.email || !formData.message) {
        showToast("Please fill in all required fields", "error");
        setIsSubmitting(false);
        return;
      }

      // Submit to Firebase
      await submitContactForm(formData);
      
      // Track contact form submission
      logContactFormSubmit(formData.subject || 'general');
      
      showToast("Thank you! Your message has been sent successfully.", "success");
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
      
    } catch (error) {
      console.error('Error submitting contact form:', error);
      showToast('Error sending message. Please try again.', "error");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <main>
      {/* Toast Notification */}
      {toast.show && (
        <div style={{
          position: "fixed",
          top: 20,
          right: 20,
          background: toast.type === "success" ? "#4CAF50" : toast.type === "error" ? "#f44336" : "#2196F3",
          color: "white",
          padding: "16px 24px",
          borderRadius: 8,
          boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
          zIndex: 1000,
          maxWidth: 400,
          fontSize: "0.95rem",
          fontWeight: 500
        }}>
          {toast.message}
        </div>
      )}

    <section className="letstalk-main-section">
      <div className="letstalk-main-inner">
        <div className="letstalk-main-left" ref={detailsRef}>
          {/* Moved Let's Talk title, label, and text here */}
            <div className="letstalk-details-header">
              <div className={`letstalk-details-subtitle subtitle-animate ${detailsVisible ? 'visible' : ''}`}>Got a project in mind ?</div>
              <h1 className={`letstalk-details-title hero-title-animate ${detailsVisible ? 'visible' : ''}`}>Let's Talk</h1>
              <p className={`letstalk-details-desc description-animate ${detailsVisible ? 'visible' : ''}`}>Reach out to discuss how we can,<br /> help you bring your project to life.</p>
            </div>

          <div className="letstalk-details-section">
            <div className={`letstalk-details-label subtitle-animate ${detailsVisible ? 'visible' : ''}`} style={{fontSize: '0.8rem'}}>Contact Details</div>
            <div className={`letstalk-details-content description-animate ${detailsVisible ? 'visible' : ''}`}>
              <a 
                href="https://mail.google.com/mail/?view=cm&fs=1&to=btwpune@gmail.com" 
                target="_blank" 
                rel="noopener noreferrer"
                style={{
                  color: '#181618',
                  textDecoration: 'none',
                  transition: 'color 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.color = '#baa170'}
                onMouseLeave={(e) => e.target.style.color = '#181618'}
              >
                btwpune@gmail.com
              </a><br />
              <a 
                href="https://wa.me/919960687225" 
                target="_blank" 
                rel="noopener noreferrer"
                style={{
                  color: '#181618',
                  textDecoration: 'none',
                  transition: 'color 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.color = '#baa170'}
                onMouseLeave={(e) => e.target.style.color = '#181618'}
              > 
                +91 9960687225
              </a>
            </div>
          </div>
          <div className="letstalk-details-divider"></div>
          <div className="letstalk-details-section">
            <div className={`letstalk-details-label subtitle-animate ${detailsVisible ? 'visible' : ''}`} style={{fontSize: '0.8rem'}}>Our Office</div>
            <div className={`letstalk-details-content description-animate ${detailsVisible ? 'visible' : ''}`}>
              29/A, 'Mihir'
              Lane No 6,<br />
               Manmohan Society,<br />
              Karve Nagar, Pune.<br />
            </div>
          </div>
          <div className="letstalk-details-divider"></div>
          <div className="letstalk-details-section">
            <div className={`letstalk-details-label subtitle-animate ${detailsVisible ? 'visible' : ''}`} style={{fontSize: '0.8rem'}}>Our Socials</div>
            <div className={`letstalk-details-content description-animate ${detailsVisible ? 'visible' : ''}`} style={{display: 'flex', gap: '16px', alignItems: 'center'}}>
              <a 
                href="https://kydoscope.com/profile/30hhc0WUmRPCquvpR0pz0Qf2irF2" 
                target="_blank" 
                rel="noopener noreferrer"
                style={{
                  color: '#181618',
                  textDecoration: 'none',
                  transition: 'color 0.2s ease',
                  display: 'inline-flex',
                  alignItems: 'center'
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#baa170'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#181618'}
                title="Kydoscope"
              >
                <svg width="20" height="20" viewBox="0 0 28 28" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7.58089 20.384H0V27.9649H7.58089V20.384Z" fill="currentColor"/>
                  <path d="M20.4543 20.3839H12.9438L18.2364 15.0914L18.2716 15.0562L23.5993 9.72842C20.6538 6.7829 15.8776 6.77116 12.9204 9.69321L7.59262 15.021V0C3.42666 0 0.0469405 3.34451 0 7.48701V15.0679H0.0234702L3.26236 11.829C3.55574 11.5356 4.02515 11.5356 4.31852 11.829L7.55742 15.0679L12.8734 20.3839V27.9648H28C28 23.7871 24.6203 20.4074 20.4543 20.3839Z" fill="currentColor"/>
                </svg>
              </a>
              <a 
                href="https://www.instagram.com/betweenthewallspune?igsh=aG56bXc2Z2Z6dmth" 
                target="_blank" 
                rel="noopener noreferrer"
                style={{
                  color: '#181618',
                  textDecoration: 'none',
                  transition: 'color 0.2s ease',
                  display: 'inline-flex',
                  alignItems: 'center'
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#baa170'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#181618'}
                title="Instagram"
              >
                <CiInstagram size={24} />
              </a>
              <a 
                href="https://www.linkedin.com/company/between-the-walls-btw/posts/?feedView=all" 
                target="_blank" 
                rel="noopener noreferrer"
                style={{
                  color: '#181618',
                  textDecoration: 'none',
                  transition: 'color 0.2s ease',
                  display: 'inline-flex',
                  alignItems: 'center'
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#baa170'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#181618'}
                title="LinkedIn"
              >
                <CiLinkedin size={24} />
              </a>
            </div>
          </div>
        </div>
        <div className="letstalk-main-right" ref={formRef}>
          <div className={`letstalk-details-label subtitle-animate ${formVisible ? 'visible' : ''}`} style={{fontSize: '0.8rem'}}>CONTACT FORM</div>
          <form className={`letstalk-form fade-in-up ${formVisible ? 'visible' : ''}`} onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column' }}>
            <input 
              type="text" 
              name="name"
              placeholder="Name" 
              className="letstalk-input" 
              value={formData.name}
              onChange={handleChange}
              required 
              style={{ order: 1 }}
            />
            <input 
              type="email" 
              name="email"
              placeholder="Contact Email" 
              className="letstalk-input" 
              value={formData.email}
              onChange={handleChange}
              required 
              style={{ order: 2 }}
            />
            <input 
              type="text" 
              name="phone"
              placeholder="Phone Number" 
              className="letstalk-input" 
              value={formData.phone}
              onChange={handleChange}
              style={{ order: 3 }}
            />
            <select 
              className="letstalk-input" 
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              required
              style={{ order: 4 }}
            >
              <option value="">Inquiry Type</option>
              <option value="general">General Inquiry</option>
              <option value="project">Project Inquiry</option>
              <option value="collaboration">Collaboration</option>
            </select>
            <textarea 
              placeholder="Your Message" 
              className="letstalk-textarea" 
              name="message"
              rows={5} 
              maxLength={500}
              value={formData.message}
              onChange={handleChange}
              required 
              style={{ order: 5 }}
            />
            <div className="letstalk-privacy" style={{display: 'flex', alignItems: 'center', gap: '10px', order: 6, marginTop: 'auto', paddingTop: '18px'}}>
              <input type="checkbox" id="privacy" required style={{width: '22px', height: '22px', margin: 0}} />
              <label htmlFor="privacy" style={{margin: 0}}>
                I consent to my details being stored and used in accordance with the{' '}
                <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ color: '#baa170', textDecoration: 'underline' }}>privacy policy</Link>
              </label>
            </div>
            <button 
              type="submit" 
              className="letstalk-submit"
              disabled={isSubmitting}
              style={{ 
                opacity: isSubmitting ? 0.7 : 1,
                cursor: isSubmitting ? "not-allowed" : "pointer",
                order: 7,
                marginTop: '18px'
              }}
            >
              {isSubmitting ? "Sending..." : "Submit"}
            </button>
          </form>
        </div>
      </div>
    </section>
    </main>
  );
}
